/**
 * @cfleet/api-client - Refresh State Channel
 *
 * A module-level pub/sub for the token-refresh lifecycle. Two independent
 * refreshers publish here: the reactive api-client auth middleware (401 path)
 * and the proactive @cfleet/auth adapter. They can overlap — e.g. a proactive
 * timer refresh running while a 401-triggered refresh is mid-retry.
 *
 * State is derived from a reference count rather than last-writer-wins so one
 * refresher's `exitRefresh()` cannot clobber another's `reconnecting` state
 * while that refresher is still in-flight.
 *
 * Consumers (e.g. AuthProvider) subscribe once via `subscribeRefreshState` and
 * surface a non-destructive "reconnecting" indicator.
 */

/**
 * Refresh lifecycle state.
 * - `idle` — no refresh in flight (or it settled).
 * - `refreshing` — a refresh attempt is in flight (first try).
 * - `reconnecting` — a transient failure occurred; retrying with backoff.
 */
export type RefreshState = 'idle' | 'refreshing' | 'reconnecting';

let currentState: RefreshState = 'idle';
/** Number of refreshers currently in-flight. */
let inFlightCount = 0;
const listeners = new Set<(state: RefreshState) => void>();

function publish(state: RefreshState): void {
  if (state === currentState) return;
  currentState = state;
  for (const listener of listeners) {
    listener(state);
  }
}

/**
 * Signal that a refresh attempt is starting.
 * The first caller transitions to `refreshing`; subsequent concurrent callers
 * are no-ops on state (state is already refreshing or reconnecting).
 */
export function enterRefresh(): void {
  inFlightCount++;
  if (inFlightCount === 1) publish('refreshing');
}

/**
 * Signal that a transient failure occurred and a retry is in progress.
 * Sets a sticky flag so state stays `reconnecting` until ALL refreshers exit.
 */
export function markReconnecting(): void {
  publish('reconnecting');
}

/**
 * Signal that a refresh attempt finished (success or failure).
 * Only transitions to `idle` once every active refresher has exited.
 */
export function exitRefresh(): void {
  inFlightCount = Math.max(0, inFlightCount - 1);
  if (inFlightCount === 0) {
    publish('idle');
  }
}

/** Current refresh state. */
export function getRefreshState(): RefreshState {
  return currentState;
}

/** Subscribe to refresh-state changes. Returns an unsubscribe function. */
export function subscribeRefreshState(listener: (state: RefreshState) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Direct state override — for tests only.
 * Production code should use enterRefresh / markReconnecting / exitRefresh.
 */
export function setRefreshState(state: RefreshState): void {
  if (state === 'idle') {
    inFlightCount = 0;
  }
  publish(state);
}

/** Test helper: reset channel to idle and drop all listeners. */
export function resetRefreshState(): void {
  currentState = 'idle';
  inFlightCount = 0;
  listeners.clear();
}
